import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, SortItem } from "namirasoft-core";
import { BranchRow, CompanyRow, FiscalYearRow, NamirasoftERPMetaDatabase } from "namirasoft-erp";
import { NSBoxSearch, NSButtonBlue, NSLoading } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";
import Styles from "./ErpScopeEdit.module.css";

interface Props extends NSARouterMakerResult, ErpRouterProps
{
    mode: ErpScopeMode;
    fields: ErpScopeField[];
    onClose: () => void;
}

interface State
{
    company_id: string | null;
    branch_id: string | null;
    fiscal_year_id: string | null;
    company_name: string | null;
    branch_name: string | null;
    fiscal_year_name: string | null;
    companies: CompanyRow[];
    branches: { [company_id: string]: BranchRow[] };
    fiscal_years: { [company_id: string]: FiscalYearRow[] };
    expanded: { [company_id: string]: boolean };
    search: string;
    loading: boolean;
    error: string | null;
}

interface NodeOptions
{
    key: string;
    depth: number;
    label: string;
    labelClass?: string;
    sub?: string | null;
    icon: string;
    selected: boolean;
    current?: boolean;
    onClick: () => void;
    expanded?: boolean;
    onToggle?: () => void;
    nodeRef?: React.RefObject<HTMLDivElement>;
}

interface ItemOptions
{
    key: string;
    label: string;
    labelClass?: string;
    sub?: string | null;
    icon: string;
    selected: boolean;
    onClick: () => void;
    nodeRef?: React.RefObject<HTMLDivElement>;
}

export class ErpScopeEdit extends Component<Props, State>
{
    private selectedNode = createRef<HTMLDivElement>();

    override state: State = (() =>
    {
        let s = ErpScope.get();
        return {
            company_id: s.company_id,
            branch_id: s.branch_id,
            fiscal_year_id: s.fiscal_year_id,
            company_name: s.company_name,
            branch_name: s.branch_name,
            fiscal_year_name: s.fiscal_year_name,
            companies: [],
            branches: {},
            fiscal_years: {},
            expanded: {},
            search: "",
            loading: true,
            error: null,
        };
    })();

    override componentDidMount(): void
    {
        this.load();
    }

    private async load(): Promise<void>
    {
        let db = new NamirasoftERPMetaDatabase();
        let { fields } = this.props;
        let showBranch = fields.includes(ErpScopeField.Branch);
        let showFiscalYear = fields.includes(ErpScopeField.FiscalYear);
        let enabled = (table: any) => new FilterItem(table, table.columns.enabled, false, FilterItemOperator.all.equals, "1");

        try
        {
            let [companies, branches, fiscalYears] = await Promise.all([
                this.props.servers.erp.company.List(
                    [enabled(db.tables.company)], null, null,
                    [new SortItem(db.tables.company, db.tables.company.columns.name, true)], null),
                showBranch
                    ? this.props.servers.erp.branch.List(
                        [enabled(db.tables.branch)], null, null,
                        [new SortItem(db.tables.branch, db.tables.branch.columns.name, true)], null)
                    : Promise.resolve({ rows: [] as BranchRow[], count: 0 }),
                showFiscalYear
                    ? this.props.servers.erp.fiscal_year.List(
                        [enabled(db.tables.fiscal_year)], null, null,
                        [new SortItem(db.tables.fiscal_year, db.tables.fiscal_year.columns.name, false)], null)
                    : Promise.resolve({ rows: [] as FiscalYearRow[], count: 0 }),
            ]);

            let branchMap: { [company_id: string]: BranchRow[] } = {};
            for (let b of branches.rows)
                (branchMap[b.company_id] ??= []).push(b);

            let fiscalYearMap: { [company_id: string]: FiscalYearRow[] } = {};
            for (let f of fiscalYears.rows)
                (fiscalYearMap[f.company_id] ??= []).push(f);

            // Expand everything when the tree is small enough to scan at a glance,
            // otherwise only the branch of the tree that is currently selected.
            let expandAll = companies.rows.length <= 5;
            let expanded: { [company_id: string]: boolean } = {};
            for (let c of companies.rows)
                if (expandAll || c.id === this.state.company_id)
                    expanded[c.id] = true;

            // Names in the stored scope may be stale; refresh them from what we just loaded.
            let company = companies.rows.find(c => c.id === this.state.company_id) ?? null;
            let branch = branches.rows.find(b => b.id === this.state.branch_id) ?? null;
            let fiscalYear = fiscalYears.rows.find(f => f.id === this.state.fiscal_year_id) ?? null;

            this.setState({
                companies: companies.rows,
                branches: branchMap,
                fiscal_years: fiscalYearMap,
                expanded,
                loading: false,
                company_name: company ? company.name : this.state.company_name,
                branch_name: branch ? branch.name : this.state.branch_name,
                fiscal_year_name: fiscalYear ? fiscalYear.name : this.state.fiscal_year_name,
            }, () => this.selectedNode.current?.scrollIntoView({ block: "nearest" }));
        }
        catch (e: any)
        {
            this.setState({ loading: false, error: e?.message ?? "Failed to load companies." });
        }
    }

    private companyName(company_id: string): string | null
    {
        let c = this.state.companies.find(x => x.id === company_id);
        return c ? c.name : null;
    }

    private selectNothing = (): void =>
    {
        this.setState({
            company_id: null, company_name: null,
            branch_id: null, branch_name: null,
            fiscal_year_id: null, fiscal_year_name: null,
            error: null,
        });
    };

    private selectCompany = (company: CompanyRow): void =>
    {
        this.setState(s =>
        {
            let same = s.company_id === company.id;
            return {
                company_id: company.id, company_name: company.name,
                branch_id: null, branch_name: null,
                fiscal_year_id: same ? s.fiscal_year_id : null,
                fiscal_year_name: same ? s.fiscal_year_name : null,
                expanded: { ...s.expanded, [company.id]: true },
                error: null,
            };
        });
    };

    private selectBranch = (branch: BranchRow): void =>
    {
        this.setState(s =>
        {
            let same = s.company_id === branch.company_id;
            return {
                company_id: branch.company_id,
                company_name: this.companyName(branch.company_id) ?? s.company_name,
                branch_id: branch.id, branch_name: branch.name,
                fiscal_year_id: same ? s.fiscal_year_id : null,
                fiscal_year_name: same ? s.fiscal_year_name : null,
                error: null,
            };
        });
    };

    private selectFiscalYear = (fiscal_year: FiscalYearRow): void =>
    {
        this.setState(s =>
        {
            let same = s.company_id === fiscal_year.company_id;
            return {
                company_id: fiscal_year.company_id,
                company_name: this.companyName(fiscal_year.company_id) ?? s.company_name,
                branch_id: same ? s.branch_id : null,
                branch_name: same ? s.branch_name : null,
                fiscal_year_id: fiscal_year.id, fiscal_year_name: fiscal_year.name,
                error: null,
            };
        });
    };

    private selectAllFiscalYears = (company: CompanyRow): void =>
    {
        this.setState(s =>
        {
            let same = s.company_id === company.id;
            return {
                company_id: company.id, company_name: company.name,
                branch_id: same ? s.branch_id : null,
                branch_name: same ? s.branch_name : null,
                fiscal_year_id: null, fiscal_year_name: null,
                error: null,
            };
        });
    };

    private toggle(company_id: string): void
    {
        this.setState(s => ({ expanded: { ...s.expanded, [company_id]: !s.expanded[company_id] } }));
    }

    private apply = (done: () => void): void =>
    {
        let { mode, fields } = this.props;
        let { company_id, branch_id, fiscal_year_id, company_name, branch_name, fiscal_year_name } = this.state;

        if (mode === ErpScopeMode.Select)
        {
            let missing: string[] = [];
            if (fields.includes(ErpScopeField.Company) && !company_id)
                missing.push("company");
            if (fields.includes(ErpScopeField.Branch) && !branch_id)
                missing.push("branch");
            if (fields.includes(ErpScopeField.FiscalYear) && !fiscal_year_id)
                missing.push("fiscal year");
            if (missing.length > 0)
            {
                this.setState({ error: `${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} required.` });
                done();
                return;
            }
        }

        ErpScope.set({
            company_id, branch_id, fiscal_year_id,
            company_name, branch_name, fiscal_year_name,
        });
        done();
        this.props.onClose();
    };

    private renderNode(o: NodeOptions): JSX.Element
    {
        return (
            <div
                key={o.key}
                ref={o.nodeRef}
                className={`${Styles.node} ${o.selected ? Styles.selected : ""} ${o.current && !o.selected ? Styles.current : ""}`}
                style={{ paddingLeft: 10 + o.depth * 18 }}
                onClick={o.onClick}
                title={o.label}
            >
                {
                    o.onToggle
                        ? <button
                            type="button"
                            className={Styles.chevron}
                            onClick={(e) => { e.stopPropagation(); o.onToggle!(); }}
                        >
                            {o.expanded ? "▾" : "▸"}
                        </button>
                        : <span className={Styles.chevron} />
                }
                <span className={Styles.icon}>{o.icon}</span>
                <span className={`${Styles.label} ${o.labelClass ?? ""}`}>{o.label}</span>
                {o.sub && <span className={Styles.sub}>{o.sub}</span>}
                <span className={Styles.check}>{o.selected ? "✓" : ""}</span>
            </div>
        );
    }

    private renderItem(o: ItemOptions): JSX.Element
    {
        return (
            <div
                key={o.key}
                ref={o.nodeRef}
                className={`${Styles.item} ${o.selected ? Styles.selected : ""}`}
                onClick={o.onClick}
                title={o.sub ? `${o.label} (${o.sub})` : o.label}
            >
                <div className={Styles.itemHead}>
                    <span className={Styles.icon}>{o.icon}</span>
                    <span className={`${Styles.label} ${o.labelClass ?? ""}`}>{o.label}</span>
                    <span className={Styles.check}>{o.selected ? "✓" : ""}</span>
                </div>
                {o.sub && <div className={Styles.itemSub}>{o.sub}</div>}
            </div>
        );
    }

    private renderCompany(company: CompanyRow, query: string): JSX.Element[]
    {
        let { mode, fields } = this.props;
        let { company_id, branch_id, fiscal_year_id, expanded } = this.state;
        let optional = mode === ErpScopeMode.Filter;
        let showBranch = fields.includes(ErpScopeField.Branch);
        let showFiscalYear = fields.includes(ErpScopeField.FiscalYear);
        let companyMatched = !query || company.name.toLowerCase().includes(query);
        // A search that hit the company name keeps all of its children visible.
        let childQuery = companyMatched ? "" : query;
        let isOpen = query ? true : !!expanded[company.id];
        let selected = company_id === company.id && branch_id === null;

        let branchRows = showBranch
            ? (this.state.branches[company.id] ?? []).filter(b => !childQuery || b.name.toLowerCase().includes(childQuery))
            : [];
        let fiscalYearRows = showFiscalYear
            ? (this.state.fiscal_years[company.id] ?? []).filter(f => !childQuery || f.name.toLowerCase().includes(childQuery))
            : [];
        let hasChildren = branchRows.length > 0 || fiscalYearRows.length > 0;

        let nodes: JSX.Element[] = [];
        nodes.push(this.renderNode({
            key: `company-${company.id}`,
            depth: 0,
            label: company.name,
            labelClass: Styles.labelCompany,
            icon: "▦",
            selected,
            current: company_id === company.id,
            nodeRef: selected ? this.selectedNode : undefined,
            onClick: () => this.selectCompany(company),
            expanded: isOpen,
            onToggle: hasChildren ? () => this.toggle(company.id) : undefined,
        }));

        if (!isOpen || !hasChildren)
            return nodes;

        if (branchRows.length > 0)
        {
            let rows = branchRows;
            let items: JSX.Element[] = [];
            if (optional)
                items.push(this.renderItem({
                    key: `br-all-${company.id}`,
                    label: "All branches",
                    labelClass: Styles.labelAll,
                    icon: "∗",
                    selected,
                    onClick: () => this.selectCompany(company),
                }));
            for (let b of rows)
                items.push(this.renderItem({
                    key: `br-${b.id}`,
                    label: b.name,
                    sub: b.country,
                    icon: "◈",
                    selected: branch_id === b.id,
                    nodeRef: branch_id === b.id ? this.selectedNode : undefined,
                    onClick: () => this.selectBranch(b),
                }));
            nodes.push(<div key={`br-group-${company.id}`} className={Styles.group} style={{ paddingLeft: 10 + 18 }}>Branches</div>);
            nodes.push(<div key={`br-items-${company.id}`} className={Styles.items}>{items}</div>);
        }

        if (fiscalYearRows.length > 0)
        {
            let rows = fiscalYearRows;
            let items: JSX.Element[] = [];
            if (optional)
                items.push(this.renderItem({
                    key: `fy-all-${company.id}`,
                    label: "All fiscal years",
                    labelClass: Styles.labelAll,
                    icon: "∗",
                    selected: company_id === company.id && fiscal_year_id === null,
                    onClick: () => this.selectAllFiscalYears(company),
                }));
            for (let f of rows)
                items.push(this.renderItem({
                    key: `fy-${f.id}`,
                    label: f.name,
                    sub: `${f.date_start} → ${f.date_end}`,
                    icon: "◷",
                    selected: fiscal_year_id === f.id,
                    onClick: () => this.selectFiscalYear(f),
                }));
            nodes.push(<div key={`fy-group-${company.id}`} className={Styles.group} style={{ paddingLeft: 10 + 18 }}>Fiscal Years</div>);
            nodes.push(<div key={`fy-items-${company.id}`} className={Styles.items}>{items}</div>);
        }

        return nodes;
    }

    private renderSummary(): JSX.Element
    {
        let { fields } = this.props;
        let { company_name, branch_name, fiscal_year_name } = this.state;
        let parts: { title: string; value: string | null }[] = [];
        if (fields.includes(ErpScopeField.Company)) parts.push({ title: "Company", value: company_name });
        if (fields.includes(ErpScopeField.Branch)) parts.push({ title: "Branch", value: branch_name });
        if (fields.includes(ErpScopeField.FiscalYear)) parts.push({ title: "Fiscal Year", value: fiscal_year_name });

        return (
            <div className={Styles.summary}>
                {
                    parts.map(p => (
                        <span key={p.title}>
                            {p.title}: <span className={`${Styles.chip} ${p.value ? "" : Styles.chipAll}`}>{p.value ?? "All"}</span>
                        </span>
                    ))
                }
            </div>
        );
    }

    override render()
    {
        let { mode } = this.props;
        let { companies, loading, error, search, company_id } = this.state;
        let optional = mode === ErpScopeMode.Filter;
        let query = search.trim().toLowerCase();

        let visible = companies.filter(c =>
        {
            if (!query) return true;
            if (c.name.toLowerCase().includes(query)) return true;
            if ((this.state.branches[c.id] ?? []).some(b => b.name.toLowerCase().includes(query))) return true;
            if ((this.state.fiscal_years[c.id] ?? []).some(f => f.name.toLowerCase().includes(query))) return true;
            return false;
        });

        let tree: JSX.Element;
        if (loading)
            tree = <NSLoading small hideTitle hideDescription />;
        else if (companies.length === 0)
            tree = <div className={Styles.empty}>No companies found.</div>;
        else if (visible.length === 0)
            tree = <div className={Styles.empty}>Nothing matches “{search.trim()}”.</div>;
        else
            tree = (
                <>
                    {
                        optional && this.renderNode({
                            key: "all",
                            depth: 0,
                            label: "All companies",
                            labelClass: Styles.labelAll,
                            icon: "∗",
                            selected: company_id === null,
                            onClick: this.selectNothing,
                        })
                    }
                    {visible.map(c => this.renderCompany(c, query))}
                </>
            );

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, width: "75vw", minWidth: 420 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "hsla(234, 64%, 22%, 1)" }}>Scope</div>

                {this.renderSummary()}

                <div className={Styles.tree}>{tree}</div>

                {error && <div style={{ color: "red", fontSize: 12 }}>{error}</div>}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 8 }}>
                    <NSBoxSearch
                        title="Search"
                        hideHeader
                        required={false}
                        placeholder="Search company, branch or fiscal year"
                        style={{ flex: 1, maxWidth: 360 }}
                        onChanged={(b) => this.setState({ search: b.getValue(false) ?? "" })}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                        <NSButtonBlue
                            title="Cancel"
                            onClick={{ action: (done) => { done(); this.props.onClose(); }, showLoading: false }}
                        />
                        <NSButtonBlue
                            title="Apply"
                            onClick={{ action: this.apply }}
                        />
                    </div>
                </div>
            </div>
        );
    }
}